// RouteBadge — small inline chip showing the recommended bookmaker
// for a single leg, plus a sharp/region marker. Designed to live next
// to the leg's odds in any leg-row component.
//
// Doctrine: this is a recommendation surface, NOT a bet-placement
// surface. STRIKZA never places bets. The badge labels which
// bookmaker the user should COPY the slip to manually.

import type { RouteEntry } from "../routing/useSlipRouting.ts";

export interface RouteBadgeProps {
  readonly route: RouteEntry | null;
  /** When true, render an em-dash placeholder instead of nothing while loading. */
  readonly placeholder?: boolean;
  /** Compact mode hides the region pill (use in tight leg rows). */
  readonly compact?: boolean;
}

export function RouteBadge({
  route,
  placeholder = false,
  compact = false,
}: RouteBadgeProps): JSX.Element | null {
  if (!route || !route.recommended) {
    if (placeholder) {
      return (
        <span
          aria-label="Bookmaker route pending"
          style={{
            fontFamily: "var(--font-mono, monospace)",
            fontSize: 10,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            color: "var(--text-quaternary, #555)",
            padding: "2px 6px",
            border: "1px solid var(--border-faint, #2a2a2a)",
            borderRadius: 4,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          —
        </span>
      );
    }
    return null;
  }

  const r = route.recommended;
  const userMarker = r.is_user_primary === true
    ? "★"
    : r.user_has_account === true
      ? "·"
      : null;

  return (
    <span
      title={
        r.is_user_primary
          ? `${r.display_name} — your primary bookmaker`
          : r.user_has_account
            ? `${r.display_name} — you have an account here`
            : `${r.display_name} (${r.region})${r.is_sharp ? " · sharp comparator" : ""}`
      }
      style={{
        fontFamily: "var(--font-mono, monospace)",
        fontSize: 10,
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        color: r.is_sharp ? "var(--accent, #b07cff)" : "var(--text-secondary, #aaa)",
        background: r.is_user_primary
          ? "var(--accent-soft, rgba(176,124,255,0.10))"
          : "var(--bg-elev-1, transparent)",
        padding: "2px 6px",
        border: `1px solid ${
          r.is_sharp ? "var(--accent, #b07cff)" : "var(--border-faint, #2a2a2a)"
        }`,
        borderRadius: 4,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        whiteSpace: "nowrap",
      }}
    >
      {userMarker !== null ? <span aria-hidden="true">{userMarker}</span> : null}
      <span>{r.display_name}</span>
      {!compact ? (
        <span
          aria-hidden="true"
          style={{
            color: "var(--text-tertiary, #666)",
            fontSize: 9,
            opacity: 0.8,
          }}
        >
          {r.region}
        </span>
      ) : null}
      {r.is_sharp && !compact ? (
        <span
          aria-label="Sharp comparator"
          title="Sharp bookmaker — used as a fair-line comparator"
          style={{ fontSize: 9, opacity: 0.9 }}
        >
          ◆
        </span>
      ) : null}
    </span>
  );
}
